import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "p-4" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { key: 3 }
const _hoisted_6 = { class: "grid-elem" }
const _hoisted_7 = { class: "popularity-bar" }
const _hoisted_8 = { class: "combo-display" }
const _hoisted_9 = { class: "synergy-indicator" }
const _hoisted_10 = { class: "synergy-bar" }

import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Card from 'primevue/card'
import ModuleTable from '@/components/module/ModuleTable.vue'
import { formatPrice } from '@/common/function'
import { MODULE_STATS, QUALITY_LIST } from '@/components/module/data'
import { api } from '@/api'

type Attr = {
  slot: string
  definitionId: string
  quality: number
}

type CombinationStat = {
  attributes: Attr[]
  avgPrice: number
  salesCount: number
  liquidityPerDay: number
  synergy?: number
  popularity?: number
  priceVariance?: number
}

type AttributeStat = {
  slot: string
  definitionId: string
  quality: number
  avgPrice: number
  salesCount: number
  liquidityPerDay: number
  priceVariance?: number
  medianPrice?: number
  popularity?: number
}

interface MarketAnalytics {
  attributes: Record<string, Record<string, Record<number, AttributeStat>>>
  combinations: CombinationStat[]
  popularPairs: Record<string, CombinationStat[]>
  synergyMatrix: Record<string, number>
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ModuleStat',
  setup(__props) {

const data = ref<MarketAnalytics | null>(null)
const lots = ref<any[]>([])
const loading = ref(false)
const error = ref('')

/* ------------------------------------------------ */
function attrKey(a: Attr) {
  return `${a.slot}*${a.definitionId}*${a.quality}`
}

function comboKey(attrs: Attr[]) {
  return attrs.map(attrKey).sort().join('|')
}

/* ------------------------------------------------ */
const comboIndex = computed(() => {
  const map = new Map<string, CombinationStat>()
  if (!data.value?.combinations) return map

  for (const c of data.value.combinations) {
    map.set(comboKey(c.attributes), c)
  }

  return map
})

/* ------------------------------------------------ */
function mapSlot(value: number) {
  if (value === 0) return 'prefix'
  if (value === 1) return 'suffix'
  if (value === 2) return 'affix'
  return 'prefix'
}

/* ------------------------------------------------ */
function normalizeAttributes(attrs: any[]): Record<string, Attr | null> {
  const result: {
    prefix: Attr | null
    suffix: Attr | null
    affix: Attr | null
  } = {
    prefix: null,
    suffix: null,
    affix: null
  }

  if (!attrs) return result

  for (const a of attrs) {
    const slot = mapSlot(a.type)
    if (!slot) continue
    result[slot] = {
      slot,
      definitionId: a.definitionId,
      quality: a.quality ?? 0
    }
  }

  return result
}

/* ------------------------------------------------ */
function findCombos(prefix: Attr | null, suffix: Attr | null, affix: Attr | null) {
  const index = comboIndex.value
  const result = { triple: null, AB: null, AC: null, BC: null }

  if (prefix && suffix) result.AB = index.get(comboKey([prefix, suffix])) ?? null
  if (prefix && affix) result.AC = index.get(comboKey([prefix, affix])) ?? null
  if (suffix && affix) result.BC = index.get(comboKey([suffix, affix])) ?? null
  if (prefix && suffix && affix)
    result.triple = index.get(comboKey([prefix, suffix, affix])) ?? null

  return result
}

/* ------------------------------------------------ */
function getSingleStat(attr: Attr | null) {
  if (!attr || !data.value?.attributes) return null
  return data.value.attributes[attr.slot]?.[attr.definitionId]?.[attr.quality] ?? null
}

/* ------------------------------------------------ */
/* Персентиль для фильтрации аномальных цен */
function percentile(arr: number[], q: number) {
  if (!arr.length) return 0
  const sorted = [...arr].sort((a, b) => a - b)
  const pos = (sorted.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  if (sorted[base + 1] !== undefined) return sorted[base] + rest * (sorted[base + 1] - sorted[base])
  return sorted[base]
}

/* ------------------------------------------------ */
function filterStatsByPercentile(stats: AttributeStat[]) {
  if (!stats.length) return stats

  // Разделяем на высоколиквидные и низколиквидные
  const highLiquidity = stats.filter(s => s.liquidityPerDay >= 1)
  const lowLiquidity = stats.filter(s => s.liquidityPerDay < 1)

  let filtered = [...stats]

  // Сначала отсекаем явные RMT выбросы (продажи по 80M+)
  filtered = filtered.filter(s => s.avgPrice < 50000000) // отсекаем все что выше 50M

  // Для высоколиквидных применяем строгую фильтрацию
  if (highLiquidity.length >= 3) {
    const prices = highLiquidity.map(s => s.avgPrice).filter(p => p > 0 && p < 50000000)
    if (prices.length) {
      const low = percentile(prices, 0.1) // Уменьшили нижний порог
      const high = percentile(prices, 0.9) // Уменьшили верхний порог
      filtered = highLiquidity.filter(
        s => s.avgPrice >= low && s.avgPrice <= high && s.avgPrice < 50000000
      )
    }
  }

  // Для низколиквидных используем более мягкую фильтрацию
  if (lowLiquidity.length) {
    const prices = lowLiquidity.map(s => s.avgPrice).filter(p => p > 0 && p < 50000000)
    if (prices.length >= 2) {
      const median = percentile(prices, 0.5)
      const maxDeviation = Math.min(median * 3, 20000000) // ограничиваем максимум 20M
      const lowLiqFiltered = lowLiquidity.filter(
        s => s.avgPrice <= maxDeviation && s.avgPrice < 50000000
      )
      filtered = [...filtered, ...lowLiqFiltered]
    }
  }

  // Дополнительная проверка на аномальные цены для редких модулей
  if (filtered.length >= 5) {
    const allPrices = filtered.map(s => s.avgPrice).filter(p => p > 0)
    const globalMedian = percentile(allPrices, 0.5)

    // Отсекаем цены которые в 10+ раз выше медианы
    filtered = filtered.filter(s => s.avgPrice <= globalMedian * 10)
  }

  // Если после фильтрации осталось мало данных, возвращаем отфильтрованные от RMT
  if (filtered.length < 2) {
    return stats.filter(s => s.avgPrice < 50000000)
  }

  return filtered
}

function estimateMarketPriceWithPairs(pairs: any, singles: any) {
  const affix = singles.C ?? { avgPrice: 0, liquidityPerDay: 0 }
  const prefix = singles.A ?? { avgPrice: 0, liquidityPerDay: 0 }
  const suffix = singles.B ?? { avgPrice: 0, liquidityPerDay: 0 }

  // Берем пары и тройку
  const triple = pairs.triple ?? null
  const AC = pairs.AC ?? null // affix + prefix
  const BC = pairs.BC ?? null // affix + suffix

  // const AB = pairs.AB ?? null // prefix + suffix

  return {
    price: triple?.avgPrice,
    liquidityPerDay: triple?.liquidityPerDay,
    salesCount: triple?.salesCount,
    affixPrice: affix?.avgPrice,
    affixLiquidityPerDay: affix?.liquidityPerDay,
    affixSalesCount: affix?.salesCount,
    AC,
    BC
  }

  // Сначала собираем все доступные цены для ограничений
  // const allPrices = [
  //   triple?.avgPrice,
  //   AC?.avgPrice,
  //   BC?.avgPrice,
  //   AB?.avgPrice,
  //   affix.avgPrice,
  //   prefix.avgPrice,
  //   suffix.avgPrice
  // ].filter(p => p > 0)

  // Тройка имеет наибольший приоритет, если есть
  // elements.push({
  //   price: triple.avgPrice,
  //   label: 'трёхкомбинация'
  // })

  // Пары с учетом синергии
  // if (AC?.liquidityPerDay > 0 && AC.avgPrice <= maxAllowedPrice) {
  //   elements.push({
  //     price: Math.min(AC.avgPrice, maxAllowedPrice),
  //     weight:
  //       AC.liquidityPerDay * (weightAffix + weightPrefix) * (synergyScore > 0.6 ? synergyBonus : 1),
  //     label: 'аффикс+префикс'
  //   })
  // }
  // if (BC?.liquidityPerDay > 0 && BC.avgPrice <= maxAllowedPrice) {
  //   elements.push({
  //     price: Math.min(BC.avgPrice, maxAllowedPrice),
  //     weight:
  //       BC.liquidityPerDay * (weightAffix + weightSuffix) * (synergyScore > 0.6 ? synergyBonus : 1),
  //     label: 'аффикс+суффикс',
  //     synergy: synergyScore
  //   })
  // }
  // if (AB?.liquidityPerDay > 0 && AB.avgPrice <= maxAllowedPrice) {
  //   elements.push({
  //     price: Math.min(AB.avgPrice, maxAllowedPrice),
  //     weight:
  //       AB.liquidityPerDay *
  //       (weightPrefix + weightSuffix) *
  //       (synergyScore > 0.6 ? synergyBonus : 1),
  //     label: 'префикс+суффикс',
  //     synergy: synergyScore
  //   })
  // }
  //
  // // Одиночные статы с ограничением цены
  // if (affix.liquidityPerDay > 0 && affix.avgPrice <= maxAllowedPrice)
  //   elements.push({
  //     price: Math.min(affix.avgPrice, maxAllowedPrice),
  //     weight: affix.liquidityPerDay * weightAffix,
  //     label: 'аффикс'
  //   })
  // if (prefix.liquidityPerDay > 0 && prefix.avgPrice <= maxAllowedPrice)
  //   elements.push({
  //     price: Math.min(prefix.avgPrice, maxAllowedPrice),
  //     weight: prefix.liquidityPerDay * weightPrefix,
  //     label: 'префикс'
  //   })
  // if (suffix.liquidityPerDay > 0 && suffix.avgPrice <= maxAllowedPrice)
  //   elements.push({
  //     price: Math.min(suffix.avgPrice, maxAllowedPrice),
  //     weight: suffix.liquidityPerDay * weightSuffix,
  //     label: 'суффикс'
  //   })
}

/* ------------------------------------------------ */
function calculateLotPriority(lot: any) {
  const attrs = normalizeAttributes(lot?.additional?.attributes)
  const pairs = findCombos(attrs.prefix, attrs.suffix, attrs.affix)
  const singles = {
    A: getSingleStat(attrs.prefix),
    B: getSingleStat(attrs.suffix),
    C: getSingleStat(attrs.affix)
  }

  if (singles.A) singles.A = filterStatsByPercentile([singles.A])[0] ?? singles.A
  if (singles.B) singles.B = filterStatsByPercentile([singles.B])[0] ?? singles.B
  if (singles.C) singles.C = filterStatsByPercentile([singles.C])[0] ?? singles.C

  const info = estimateMarketPriceWithPairs(pairs, singles)

  return {
    ...lot,
    information: info,
    prefix: {
      ...attrs.prefix,
      name: getStatName(attrs.prefix?.definitionId, attrs.prefix?.slot)?.nameRu
    },
    suffix: {
      ...attrs.suffix,
      name: getStatName(attrs.suffix?.definitionId, attrs.suffix?.slot)?.nameRu
    },
    affix: {
      ...attrs.affix,
      name: getStatName(attrs.affix?.definitionId, attrs.affix?.slot)?.nameRu
    }
  }
}

/* ------------------------------------------------ */
async function fetchModulesStat() {
  loading.value = true
  try {
    const res = await api.get('/module-stat/analytics')
    data.value = res.data
  } catch (e) {
    error.value = 'API error'
  }
  loading.value = false
}

/* ------------------------------------------------ */
async function fetchModulesLots() {
  loading.value = true
  try {
    const res = await api.get('/module-stat/lots')
    const rawLots = res.data ?? []
    lots.value = rawLots.filter(i => i.buyoutPrice).map(calculateLotPriority)
  } finally {
    loading.value = false
  }
}

/* ------------------------------------------------ */
onMounted(async () => {
  await fetchModulesStat()
  await fetchModulesLots()
})

/* ------------------------------------------------ */
const getStatName = (definitionId, slot) => {
  const cleaned = definitionId?.replace(/_(pre|suf|aff)$/g, '').replace(/_(pre|suf|aff)_?/g, '')
  const stat = MODULE_STATS.find(s => s.id === cleaned && s.slots?.includes(slot))
  return stat || cleaned
}

const getQuality = quality => QUALITY_LIST.find(q => q.value === quality)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, "Loading..."))
      : _createCommentVNode("", true),
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    (lots.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(ModuleTable, { lots: lots.value }, null, 8, ["lots"])
        ]))
      : _createCommentVNode("", true),
    (data.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (data.value?.attributes)
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(data.value.attributes, (fixItem, fix) => {
                return (_openBlock(), _createElementBlock("div", { key: fix }, [
                  _createElementVNode("h1", null, _toDisplayString(fix), 1),
                  _createVNode(_unref(Card), { style: {"background-color":"#4c515e","margin-bottom":"40px"} }, {
                    content: _withCtx(() => [
                      _createElementVNode("div", _hoisted_6, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fixItem, (elem, attr, index) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: `${fix}-${attr || 'unknown'}-${index}`
                          }, [
                            _createTextVNode(_toDisplayString(getStatName(attr, fix)?.nameRu) + " (" + _toDisplayString(getStatName(attr, fix)?.parameterRu) + ") ", 1),
                            _createVNode(_unref(DataTable), {
                              value: Object.values(elem)
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Column), {
                                  field: "quality",
                                  header: "Quality"
                                }),
                                _createVNode(_unref(Column), {
                                  field: "avgPrice",
                                  header: "Avg Price"
                                }, {
                                  body: _withCtx(({ data }) => [
                                    _createTextVNode(_toDisplayString(_unref(formatPrice)(data.avgPrice)), 1)
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_unref(Column), {
                                  field: "salesCount",
                                  header: "Sales Count"
                                }),
                                _createVNode(_unref(Column), {
                                  field: "liquidityPerDay",
                                  header: "Liquidity/Day"
                                }, {
                                  body: _withCtx(({ data }) => [
                                    _createTextVNode(_toDisplayString(data.liquidityPerDay?.toFixed(2)), 1)
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_unref(Column), {
                                  field: "popularity",
                                  header: "Popularity"
                                }, {
                                  body: _withCtx(({ data }) => [
                                    _createElementVNode("div", _hoisted_7, [
                                      _createElementVNode("div", {
                                        class: "popularity-fill",
                                        style: _normalizeStyle({
                              width: `${(data.popularity || 0) * 100}%`
                            })
                                      }, null, 4),
                                      _createElementVNode("span", null, _toDisplayString(((data.popularity || 0) * 100).toFixed(1)) + "%", 1)
                                    ])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 2
                            }, 1032, ["value"])
                          ]))
                        }), 128))
                      ])
                    ]),
                    _: 2
                  }, 1024)
                ]))
              }), 128))
            : _createCommentVNode("", true),
          (data.value?.popularPairs)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _cache[0] || (_cache[0] = _createElementVNode("h1", null, "Популярные комбинации", -1)),
                _createVNode(_unref(Card), { style: {"background-color":"#4c515e","margin-bottom":"40px"} }, {
                  content: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.value.popularPairs, (pairs, category) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: category,
                        class: "mb-6"
                      }, [
                        _createElementVNode("h3", null, _toDisplayString(category), 1),
                        _createVNode(_unref(DataTable), {
                          value: 
                  Array.isArray(pairs) ? pairs.slice(0, 10) : Object.values(pairs).slice(0, 10)
                
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Column), { header: "Комбинация" }, {
                              body: _withCtx(({ data }) => [
                                _createElementVNode("div", _hoisted_8, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.attributes, (attr, idx) => {
                                    return (_openBlock(), _createElementBlock("span", {
                                      key: attrKey(attr) || `attr-${idx}`,
                                      class: _normalizeClass(["attr-badge", `quality-${attr.quality}`])
                                    }, _toDisplayString(getStatName(attr.definitionId, attr.slot)?.nameRu), 3))
                                  }), 128))
                                ])
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(Column), {
                              field: "avgPrice",
                              header: "Avg Price"
                            }, {
                              body: _withCtx(({ data }) => [
                                _createTextVNode(_toDisplayString(_unref(formatPrice)(data.avgPrice)), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(Column), {
                              field: "salesCount",
                              header: "Sales"
                            }),
                            _createVNode(_unref(Column), {
                              field: "liquidityPerDay",
                              header: "Liquidity/Day"
                            }, {
                              body: _withCtx(({ data }) => [
                                _createTextVNode(_toDisplayString(data.liquidityPerDay?.toFixed(2)), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(Column), {
                              field: "synergy",
                              header: "Синергия"
                            }, {
                              body: _withCtx(({ data }) => [
                                _createElementVNode("div", _hoisted_9, [
                                  _createElementVNode("div", _hoisted_10, [
                                    _createElementVNode("div", {
                                      class: "synergy-fill",
                                      style: _normalizeStyle({ width: `${(data.synergy || 0) * 100}%` })
                                    }, null, 4)
                                  ]),
                                  _createElementVNode("span", null, _toDisplayString(((data.synergy || 0) * 100).toFixed(0)) + "%", 1)
                                ])
                              ]),
                              _: 1
                            })
                          ]),
                          _: 2
                        }, 1032, ["value"])
                      ]))
                    }), 128))
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})